/* ============================================================
   moduleLabels.js — permission module slug → the name the sidebar uses.

   The permission catalogue names modules by backend slug (`hiring_managers`,
   `cv_import`). Access Control has to show the same words as the sidebar, or
   an admin can't tell which tab a tick grants. So the label and the order come
   from ROUTES (`permission: '<module>.view'`); the few modules that have no
   sidebar tab are listed in EXTRA. Display only — slugs stay the source of truth.
   ============================================================ */

import { NAV_GROUPS, ROUTES } from '../app/routes'

// Modules that gate something other than a sidebar tab.
const EXTRA = {
  rbac_users: { title: 'Access Control (Users & Roles)', group: 'System' },
  role_preview: { title: 'Role Preview', group: 'System' },
  // Settings → View opens the Settings page only; each tab has its own module.
  settings_general: { title: 'Settings General', group: 'System' },
  // View = Approvals page; Manage = All approvals tab + decide on behalf.
  approvals: { title: 'Approvals', group: 'Recruitment' },
  // Edit = change another user's email & password (Settings > Users).
  user_credentials: { title: 'User Email & Password', group: 'System' },
}

const OTHER_GROUP = 'Other'

const BY_MODULE = (() => {
  const map = new Map()
  ROUTES.forEach((r, order) => {
    const slug = typeof r.permission === 'string' ? r.permission.split('.')[0] : null
    if (slug && !map.has(slug)) map.set(slug, { title: r.title, group: r.group, order })
  })
  Object.entries(EXTRA).forEach(([slug, meta], i) => {
    if (!map.has(slug)) map.set(slug, { ...meta, order: ROUTES.length + i })
  })
  return map
})()

/** Fallback for a module the backend adds before the UI knows it: "cv_bank" → "Cv Bank". */
function humanise(slug) {
  return String(slug || '')
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** 'hiring_managers' → 'Recruiter Managers', 'cv_import' → 'CV Import'. */
export function moduleLabel(slug) {
  return BY_MODULE.get(slug)?.title ?? humanise(slug)
}

/** Sidebar section the module's tab sits in; 'Other' when it has none. */
export function moduleGroup(slug) {
  return BY_MODULE.get(slug)?.group ?? OTHER_GROUP
}

/** Sidebar order: by section, then by position in the sidebar; unknown slugs last. */
export function sortModules(slugs) {
  const groupRank = (s) => {
    const i = NAV_GROUPS.indexOf(moduleGroup(s))
    return i === -1 ? NAV_GROUPS.length : i
  }
  const rank = (s) => BY_MODULE.get(s)?.order ?? Number.MAX_SAFE_INTEGER
  return [...slugs].sort((a, b) => groupRank(a) - groupRank(b) || rank(a) - rank(b) || a.localeCompare(b))
}
